import { useState } from "react";
import { Clock, FileText, Lock, Users } from "lucide-react";
import type { ProviderCondition } from "../config/energyProvider";
import { cn } from "@/lib/utils";
import { STRINGS } from "@/i18n";
import {
  describeCondition,
  type ConditionDescription,
  type ConditionIcon,
} from "@/utils/providerConditions";

const ICONS: Record<ConditionIcon, typeof Clock> = {
  clock: Clock,
  lock: Lock,
  document: FileText,
  members: Users,
};

// Green for settlement and a fixed formula, red for conditions the reader should check.
const ICON_CLASSES: Record<ConditionIcon, string> = {
  clock: "text-emerald-600 dark:text-emerald-400",
  lock: "text-emerald-600 dark:text-emerald-400",
  document: "text-rose-600 dark:text-rose-400",
  members: "text-rose-600 dark:text-rose-400",
};

export function ConditionChip({
  description,
  className,
}: {
  description: ConditionDescription;
  className?: string;
}) {
  const Icon = description.icon ? ICONS[description.icon] : null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-0.5 text-[13px] font-medium",
        description.tone === "positive"
          ? "border-transparent bg-primary/10 text-primary"
          : "border-border bg-muted text-foreground",
        className
      )}
    >
      {Icon && description.icon && (
        <Icon
          className={cn("h-3.5 w-3.5 shrink-0", ICON_CLASSES[description.icon])}
          aria-hidden="true"
        />
      )}
      {description.label}
    </span>
  );
}

/**
 * The chips of a provider row, in the order of the providers file. With `limit`, a longer
 * list shows its first `limit - 1` chips plus an "x meer" chip; that chip reveals the rest
 * and then reads "Minder" at the end of the list, to hide them again.
 */
export function ProviderChips({
  conditions,
  className,
  limit,
}: {
  conditions: ProviderCondition[];
  className?: string;
  limit?: number;
}) {
  const [showAll, setShowAll] = useState(false);
  if (conditions.length === 0) return null;

  const canCollapse = limit !== undefined && conditions.length > limit;
  const visible = canCollapse && !showAll ? conditions.slice(0, limit - 1) : conditions;
  const hiddenCount = conditions.length - visible.length;
  const t = STRINGS.providers;

  return (
    <ul className={cn("flex flex-wrap gap-1.5", className)}>
      {visible.map((condition, index) => (
        // A flex item, so chips with and without an icon line up at the top.
        <li key={index} className="flex">
          <ConditionChip description={describeCondition(condition)} />
        </li>
      ))}
      {canCollapse && (
        // One button for both states, always last: it keeps keyboard focus when it toggles.
        <li key="toggle" className="flex">
          <button
            type="button"
            aria-expanded={showAll}
            aria-label={
              showAll ? t.fewerConditionsLabel : t.moreConditionsLabel(hiddenCount)
            }
            onClick={(event) => {
              // Inside a clickable row, toggling chips must not toggle the row as well.
              event.stopPropagation();
              setShowAll((previous) => !previous);
            }}
            className="relative inline-flex items-center rounded-md border border-border bg-card px-2 py-0.5 text-[13px] font-medium text-primary before:absolute before:-inset-x-1 before:-inset-y-2.5 before:content-[''] hover:bg-muted focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            {showAll ? t.fewerConditions : t.moreConditions(hiddenCount)}
          </button>
        </li>
      )}
    </ul>
  );
}
